import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import shuffle from 'lodash/shuffle';
import classNames from 'classnames';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import { DataType } from '@vgtv/api-client/lib/component';

import { useAppDispatch } from '../../modules/hooks';
import { fetchSvpAssets } from '../../modules/assets';
import { useViewportType } from '../utils/useViewportType';
import { isTouchDevice } from '../../utils/device';
import { getComponentDataFetchLimit } from '../PageComponents/getComponentDataFetchLimit';
import { COMPONENT_TYPE_GALLERY } from '../../consts';
import { VGTVCategory } from '../../types';
import { getImageSources } from '../../helpers/images';
import {
  getAssetMainImage,
  getAssetVerticalImage,
} from '../../shared/helpers/assetHelpers';

import { AssetImage } from './AssetImage/AssetImage';

import * as styles from './AssetsGallery.module.scss';

export interface AssetsGalleryProps {
  className?: string;
  variant?: 'plus' | 'sport';
}

export const AssetsGallery: FC<AssetsGalleryProps> = ({
  className,
  variant = 'plus',
}) => {
  const [assets, setAssets] = useState<SvpAssetAdditional[]>([]);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const { viewportType } = useViewportType();

  useEffect(() => {
    if (isTouchDevice) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    function onMouseMove(event: MouseEvent) {
      if (containerRef.current) {
        containerRef.current.style.setProperty(
          '--client-x',
          (event.clientX / viewportWidth).toString()
        );

        containerRef.current.style.setProperty(
          '--client-y',
          (event.clientY / viewportHeight).toString()
        );
      }
    }

    document.addEventListener('mousemove', onMouseMove, { passive: true });

    return () => document.removeEventListener('mousemove', onMouseMove);
  }, []);

  const fetchAssets = useCallback(async () => {
    const categoryId =
      variant === 'sport' ? VGTVCategory.SPORT : VGTVCategory.DOCUMENTARY;

    const {
      _embedded: { assets },
    } = await dispatch(
      fetchSvpAssets({
        vendor: 'vgtv',
        // WARNING: adding query parameters to this FAKE url will result in error thrown by the URL constructor
        dataUrl: '/search', // matches the /search endpoint
        filter: [
          `categoryId::${categoryId}`,
          `additional.access::${variant}`,
        ].join('|'),
        limit: getComponentDataFetchLimit({
          componentType: COMPONENT_TYPE_GALLERY,
          dataType: DataType.ASSETS,
          viewportType,
        }),
      })
    ).unwrap();

    const shuffled = shuffle(assets);

    setAssets([...shuffled, ...shuffled]);
  }, [variant, dispatch, viewportType]);

  useEffect(() => {
    fetchAssets().catch(console.error);
  }, [fetchAssets]);

  return (
    <div className={classNames(styles.container, className)} ref={containerRef}>
      <div className={styles.rotationPane}>
        <div className={styles.positioningPane}>
          <div className={styles.animationPane}>
            {assets.map((asset, index) => (
              <AssetImage
                className={styles.image}
                key={index}
                imageSources={
                  variant === 'sport'
                    ? getImageSources({
                        imageUrl: getAssetMainImage(asset),
                        imagePreset: 'assetsSlider',
                        aspectRatio: 'horizontal',
                      })
                    : getImageSources({
                        imageUrl: getAssetVerticalImage(asset),
                        imagePreset: 'assetsSliderVertical',
                        aspectRatio: 'vertical',
                      })
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
